/**
 * Accept an invitation: pick a name and a password, and be signed in.
 *
 * THE EMAIL AND THE ROLE ARE READ BACK FROM THE TOKEN AND RENDERED AS TEXT. They
 * are the invitation, not defaults for it. An editable address field would let
 * whoever holds a forwarded link register at an address of their own choosing,
 * and an editable role field would let them decide what they get — which is the
 * same bug twice, and the reason `GET /auth/invite` exists at all rather than the
 * link simply leading to a signup form.
 *
 * SHOWING THEM IS NOT DECORATION EITHER. Somebody who forwards an invite to a
 * colleague, or who has two addresses, needs to see which one this link is for
 * before choosing a password for it. And "you are being given viewer access" is
 * information a person is entitled to before they accept.
 *
 * THE INVITE IS READ ON MOUNT AND THE FORM WAITS FOR IT. A form that renders
 * first and labels itself afterwards invites somebody to type a password for the
 * wrong account, and `GET /auth/invite` is also the only way to find out that a
 * link is dead without spending it.
 *
 * THE TOKEN IS TAKEN OUT OF THE ADDRESS BAR ON ARRIVAL, for the same reason as on
 * the reset screen and with more at stake: this link creates an account. See
 * `useLinkToken`, which captures it before it scrubs the URL, because App derives
 * the prop from the string being scrubbed.
 *
 * SUCCESS IS A SESSION, SO THIS ONE NAVIGATES. `POST /auth/accept-invite` returns
 * the user and sets the cookie, so there is nothing left to sign in to — the
 * screen calls `refresh()` and goes to the panel. ResetPassword is the opposite
 * case and offers a link instead, because that route sets no cookie.
 */
import { useEffect, useState } from 'react'
import { ApiError } from '@/api'
import { auth, type InvitePreview } from '@/lib/auth'
import { useAuth } from '@/components/auth-provider'
import { AuthShell } from '@/components/auth-shell'
import { Link, navigate, useLinkToken } from '@/components/app-sidebar'
import { PasswordField, passwordPairProblem } from '@/components/password-field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** What each role gets, in one line, for somebody who has never seen this panel. */
const ROLE_MEANS: Record<InvitePreview['role'], string> = {
  admin: 'Full access, including inviting and removing other people.',
  power: 'Full access to calls and configuration. Cannot manage people.',
  viewer: 'Read everything, change nothing. Cannot start a call.',
}

export function AcceptInvitePage({ token: fromLink }: { token: string | null }) {
  // Captured on the first render and then gone from the URL. Everything below
  // reads this and never `fromLink`.
  const token = useLinkToken(fromLink)
  const { refresh } = useAuth()
  const [invite, setInvite] = useState<InvitePreview | null>(null)
  const [readError, setReadError] = useState<{ expired: boolean; message: string } | null>(null)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (token === null) return
    const ac = new AbortController()
    auth
      .readInvite(token, ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setInvite(res)
        // Prefilled only if the invite carried one, and still editable: the
        // person accepting knows their own name better than the admin who typed
        // it into an invite form.
        setName(res.name ?? '')
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setReadError({
          expired: e instanceof ApiError && e.code === 'token_expired',
          message: e instanceof Error ? e.message : String(e),
        })
      })
    return () => ac.abort()
  }, [token])

  if (token === null) {
    return (
      <AuthShell title="This invitation is incomplete">
        <p className="text-sm text-muted-foreground">
          An invitation carries a token in its address and this one has none. Ask whoever
          invited you to send it again — email clients sometimes break the link across two
          lines.
        </p>
      </AuthShell>
    )
  }

  if (readError) {
    return (
      <AuthShell
        title={readError.expired ? 'This invitation has expired' : 'This invitation is not valid'}
        footer={
          <Link to="/login" className="underline underline-offset-4">
            Already have an account? Sign in
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground">
          {readError.expired
            ? 'Invitations are deliberately short-lived, and this one has either run out or already been used. An administrator can send a new one from the Users screen.'
            : readError.message}
        </p>
      </AuthShell>
    )
  }

  if (!invite) {
    return (
      <AuthShell title="Accept your invitation">
        <p className="text-sm text-muted-foreground" role="status">
          Reading the invitation&hellip;
        </p>
      </AuthShell>
    )
  }

  const problem = passwordPairProblem(password, confirm)
  const ready = name.trim() !== '' && problem === null

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || !ready) return
    setBusy(true)
    setError(null)
    auth
      .acceptInvite({ token, name: name.trim(), password }, AbortSignal.timeout(20_000))
      .then(() => {
        // The cookie is set. Ask the server who we are — the same reason the
        // login screen does, in auth-provider.tsx — then leave.
        refresh()
        navigate('/')
      })
      .catch((err: unknown) => {
        setBusy(false)
        if (err instanceof ApiError && err.code === 'token_expired') {
          setReadError({
            expired: true,
            message: err.message,
          })
          return
        }
        // weak_password lands here, and its message names the rule that failed.
        setError(err instanceof Error ? err.message : String(err))
      })
  }

  return (
    <AuthShell
      title="Accept your invitation"
      intro="Two things to set, and then you are in."
      footer={
        <Link to="/login" className="underline underline-offset-4">
          Already have an account? Sign in
        </Link>
      }
    >
      {/* READ-ONLY, AND NOT A DISABLED FIELD. A greyed-out input says "you could
          change this, but not now"; this is neither editable now nor later, so it
          is not rendered as a control at all. */}
      <dl className="mb-5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 rounded-md border bg-muted/40 p-3 text-sm">
        <dt className="text-muted-foreground">Account</dt>
        <dd className="mono min-w-0 break-all">{invite.email}</dd>
        <dt className="text-muted-foreground">Role</dt>
        <dd className="min-w-0">
          <span className="mono">{invite.role}</span>
          <span className="block text-xs text-muted-foreground">{ROLE_MEANS[invite.role]}</span>
        </dd>
      </dl>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="field gap-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            placeholder="How you should appear to colleagues"
            required
            autoFocus
          />
        </div>

        <PasswordField
          id="password"
          label="Choose a password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          showRule
        />
        <PasswordField
          id="confirm"
          label="Password again"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />

        {error && <p className="err text-sm">{error}</p>}

        {!error && problem && password !== '' && confirm !== '' && (
          <p className="text-sm text-muted-foreground">{problem}</p>
        )}

        <Button type="submit" disabled={busy || !ready}>
          {busy ? 'Setting up…' : 'Accept and sign in'}
        </Button>
      </form>
    </AuthShell>
  )
}
